import { useQueryClient } from '@tanstack/react-query'
import {
  Card,
  Checkbox,
  Col,
  DatePicker,
  Flex,
  Form,
  Input,
  Row,
  Select,
  UploadFile
} from 'antd'
import { FormProps } from 'antd/lib'
import dayjs, { Dayjs } from 'dayjs'
import { useEffect } from 'react'

import {
  usePersonsServiceGetGuardianshipEndpoint,
  usePersonsServiceGetGuardianshipEndpointKey,
  usePersonsServiceUpdateGuardianshipMediaFilesEndpoint
} from 'src/api/queries'
import {
  ApiError,
  GetPersonByIdnpResponse,
  InvestigationStatus
} from 'src/api/requests'
import { requiredRules } from 'src/app-constants'
import {
  CountrySelect,
  CUATMTreeSelect,
  CustomItemRender,
  GenderSelect,
  Upload
} from 'src/components'
import { useFileHandler } from 'src/hooks'
import { dayMonthYearBackSlash, yearMonthDaySlash } from 'src/libs'
import { useLocales } from 'src/locales'
import { formErrorHandler, getLocalizedTitle } from 'src/utils'

import { useStyles } from './styles'

type Props = {
  guardianshipId?: string
  data?: GetPersonByIdnpResponse
} & FormProps

export const TutorDetails = ({ data, guardianshipId }: Props) => {
  const { t, currentLang } = useLocales()

  const [form] = Form.useForm()

  const { styles } = useStyles()

  const residenceCountry = Form.useWatch(['residenceAddress', 'country'], form)

  const queryClient = useQueryClient()

  const statusOptions = Object.values(InvestigationStatus).map((item) => ({
    value: item,
    label: t(`persons.${item}`)
  }))

  const { setExistingFiles, onFileChange, saveFiles } = useFileHandler({
    form,
    folder: `person-${data?.id}`
  })

  const { data: files } = usePersonsServiceGetGuardianshipEndpoint(
    {
      id: String(guardianshipId)
    },
    [String(guardianshipId)],
    {
      enabled: !!guardianshipId
    }
  )

  const { mutate } = usePersonsServiceUpdateGuardianshipMediaFilesEndpoint({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [
          usePersonsServiceGetGuardianshipEndpointKey,
          String(guardianshipId)
        ]
      })
    },
    onError: (error: ApiError) => formErrorHandler(error.body.errors)
  })

  const docs = Form.useWatch('docs', form)

  const onSubmit = async () => {
    const mediaItems = await saveFiles()

    mutate({
      guardianshipId: String(guardianshipId),
      requestBody: {
        mediaItems
      }
    })
  }

  const onRemove = async (file: UploadFile) => {
    const updatedFileList = docs?.filter(
      (item: { uid: string }) => item.uid !== file.uid
    )

    const remainingFileIds = updatedFileList?.map(
      (item: { uid: string }) => item.uid
    ) as string[]

    mutate({
      guardianshipId: String(guardianshipId),
      requestBody: {
        mediaItems: remainingFileIds
      }
    })
    setExistingFiles(remainingFileIds)
  }

  useEffect(() => {
    if (data && files) {
      const existingFileIds = files?.mediaFiles
        ?.map((file) => file?.id)
        .filter(Boolean) as string[]
      setExistingFiles(existingFileIds)

      form.setFieldsValue({
        ...data,
        currentAddress: {
          ...data?.currentAddress,
          cuatmLocalityId: getLocalizedTitle({
            item: data?.currentAddress?.cuatmLocality,
            t,
            currentLang: currentLang.value
          })
        },
        residenceAddress: {
          ...data?.residenceAddress,
          cuatmLocalityId: getLocalizedTitle({
            item: data?.residenceAddress?.cuatmLocality,
            currentLang: currentLang.value,
            t
          })
        },
        citizenship: data?.residenceAddress?.country,
        docs: files?.mediaFiles?.map((file) => ({
          uid: file?.id,
          name: file?.name
        }))
      })
    }
  }, [data, files])

  return (
    <Card className={styles.cardWrapper} bordered={false}>
      <Card className={styles.card}>
        <Form layout="vertical" form={form} requiredMark={false} disabled>
          <Flex vertical gap={16}>
            <Row gutter={[16, 24]}>
              <Col xs={24} lg={6}>
                <Form.Item
                  name="lastName"
                  label={t('form.last_name')}
                  rules={requiredRules}
                >
                  <Input placeholder={t('form.last_name')} />
                </Form.Item>
              </Col>

              <Col xs={24} lg={6}>
                <Form.Item
                  name="firstName"
                  label={t('form.first_name')}
                  rules={requiredRules}
                >
                  <Input placeholder={t('form.first_name')} />
                </Form.Item>
              </Col>

              {data?.simeCode && (
                <Col xs={24} lg={6}>
                  <Form.Item
                    name="simeCode"
                    label={t('common.sime_code')}
                    rules={requiredRules}
                  >
                    <Input placeholder={t('common.sime_code')} />
                  </Form.Item>
                </Col>
              )}

              <Col xs={24} lg={6}>
                <Form.Item
                  name="tarvCode"
                  label={t('common.tarv_code')}
                  rules={requiredRules}
                >
                  <Input placeholder={t('common.tarv_code')} />
                </Form.Item>
              </Col>

              <Col xs={24} lg={6}>
                <Form.Item
                  name="citizenship"
                  label={t('common.citizenship')}
                  rules={requiredRules}
                >
                  <CountrySelect
                    popupMatchSelectWidth
                    placeholder={t('common.citizenship')}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} lg={6}>
                <Form.Item name="idnp" label={t('form.idnp')}>
                  <Input placeholder={t('form.idnp')} />
                </Form.Item>
              </Col>

              <Col xs={24} lg={6}>
                <Form.Item
                  name="birthDate"
                  label={t('form.birth_date')}
                  rules={requiredRules}
                  normalize={(m: Dayjs) => {
                    return dayjs(m).format(yearMonthDaySlash)
                  }}
                  getValueProps={(value) => ({
                    value: value ? dayjs(value) : undefined
                  })}
                >
                  <DatePicker
                    format={dayMonthYearBackSlash}
                    className="full-width"
                    placeholder={t('form.birth_date')}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} lg={6}>
                <Form.Item
                  name="gender"
                  label={t('form.gender')}
                  rules={requiredRules}
                >
                  <GenderSelect />
                </Form.Item>
              </Col>

              <Col span={24} lg={12}>
                <Row gutter={[16, 10]}>
                  <Col span={24}>
                    <Form.Item
                      name={['currentAddress', 'country']}
                      label={t('form.address')}
                    >
                      <CountrySelect
                        disabled
                        placeholder={t('common.country')}
                      />
                    </Form.Item>
                  </Col>

                  <Col span={24}>
                    <Form.Item name={['currentAddress', 'cuatmLocalityId']}>
                      <CUATMTreeSelect
                        placeholder={t('organizational_structure.city')}
                      />
                    </Form.Item>
                  </Col>

                  <Col span={24}>
                    <Form.Item name={['currentAddress', 'street']}>
                      <Input placeholder="bul. Moscovei" />
                    </Form.Item>
                  </Col>

                  <Col span={12}>
                    <Form.Item name={['currentAddress', 'streetNumber']}>
                      <Input placeholder="5/5" />
                    </Form.Item>
                  </Col>

                  <Col span={12}>
                    <Form.Item name={['currentAddress', 'apartmentNumber']}>
                      <Input placeholder="ap.90" />
                    </Form.Item>
                  </Col>
                </Row>
              </Col>

              <Col span={24} lg={12}>
                <Row gutter={[16, 10]}>
                  <Col span={24}>
                    <Form.Item
                      name={['residenceAddress', 'country']}
                      label={t('cases.residential_address')}
                    >
                      <CountrySelect placeholder={t('common.country')} />
                    </Form.Item>
                  </Col>

                  {residenceCountry === 'md' && (
                    <Col span={24}>
                      <Form.Item name={['residenceAddress', 'cuatmLocalityId']}>
                        <CUATMTreeSelect
                          placeholder={t('organizational_structure.city')}
                        />
                      </Form.Item>
                    </Col>
                  )}

                  <Col span={24}>
                    <Form.Item name={['residenceAddress', 'street']}>
                      <Input placeholder="bul. Moscovei" />
                    </Form.Item>
                  </Col>

                  <Col span={12}>
                    <Form.Item name={['residenceAddress', 'streetNumber']}>
                      <Input placeholder="5/5" />
                    </Form.Item>
                  </Col>

                  <Col span={12}>
                    <Form.Item name={['residenceAddress', 'apartmentNumber']}>
                      <Input placeholder="ap.90" />
                    </Form.Item>
                  </Col>
                </Row>
              </Col>

              <Col span={24}>
                <Form.Item
                  valuePropName="checked"
                  name="isResidenceAddressSameAsCurrent"
                >
                  <Checkbox>
                    {t('persons.residence_address_same_current_address')}
                  </Checkbox>
                </Form.Item>
              </Col>

              <Col xs={24} lg={12}>
                <Form.Item
                  name="phone"
                  rules={requiredRules}
                  label={t('common.phone')}
                >
                  <Input placeholder={t('common.phone')} />
                </Form.Item>
              </Col>

              <Col xs={24} lg={12}>
                <Form.Item
                  name="email"
                  rules={requiredRules?.concat({
                    type: 'email'
                  })}
                  label={t('form.email_address')}
                >
                  <Input placeholder={t('form.email_address')} />
                </Form.Item>
              </Col>

              <Col xs={24} lg={12}>
                <Form.Item name="investigationStatus" label={t('form.status')}>
                  <Select
                    options={statusOptions}
                    placeholder={t('form.status')}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} lg={12}>
                <Form.Item
                  valuePropName="checked"
                  name="wasInPrison"
                  label={<></>}
                >
                  <Checkbox>{t('cases.was_in_prison')}</Checkbox>
                </Form.Item>
              </Col>

              <Col span={24}>
                <Form.Item name="notes" label={t('form.notes')}>
                  <Input.TextArea
                    className={styles.textarea}
                    placeholder={t('form.notes')}
                  />
                </Form.Item>
              </Col>

              <Col span={24}>
                <Form.Item
                  name="docs"
                  label={t('stock_applications.uploaded_documents')}
                >
                  <Upload
                    disabled={false}
                    fileList={docs}
                    onChange={(info) => {
                      onFileChange(info)
                      onSubmit()
                    }}
                    customRequest={() => null}
                    itemRender={(_, file, __, { remove }) => (
                      <CustomItemRender
                        fileCount={docs?.length}
                        file={file}
                        onRemove={() => {
                          remove()
                          onRemove(file)
                        }}
                      />
                    )}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Flex>
        </Form>
      </Card>
    </Card>
  )
}
